import { MouseEventHandler, ReactNode, useCallback, useState } from "react";
import { useLocation, Link as RouterLink } from "react-router-dom";

import Box from "@mui/material/Box";
import Hidden from "@mui/material/Hidden";
import Link from "@mui/material/Link";
import Typography from "@mui/material/Typography";

import ContactTooltip, { ContactSection } from "./ContactTooltip";
import type { SidebarNavigationItem } from "./SidebarNavigationItem";

const LOGO_HEIGHT = 136;
const LOGO_PADDING_TOP = 8;
const BOTTOM_SECTION_ELEMENT_HEIGHT = 40;

export interface SidebarProps {
  logo?: ReactNode;
  navigationItems: SidebarNavigationItem[];
  bottomNavigationItems?: { name: string; href: string }[];
  signOut: () => Promise<void>;
  onChangePage?: MouseEventHandler;
  contacts?: ContactSection[];
}

export const SidebarView = ({
  logo,
  navigationItems,
  bottomNavigationItems,
  onChangePage,
  signOut,
  contacts,
}: SidebarProps) => {
  const { pathname } = useLocation();
  const [tooltipOpen, setTooltipOpen] = useState(false);

  const handleTooltipClose = () => {
    setTooltipOpen(false);
  };

  const handleTooltipOpen = () => {
    setTooltipOpen(true);
  };

  const comparePathNames = useCallback(
    (currentPathname: string, drawerItemPathName: string) => {
      // Pathname contains at least one slash and path name but currentPathname may contain more than one e.g. /policies/{locator}
      return currentPathname.split("/")[1] === drawerItemPathName.split("/")[1];
    },
    []
  );

  return (
    <Box
      sx={{
        height: "100%",
        width: {
          md: 140,
          lg: 180,
        },
        paddingY: {
          xs: 4.5,
          md: 2,
        },
        paddingX: {
          xs: 3,
          md: 0,
        },
        position: {
          xs: "static",
          md: "fixed",
        },
        zIndex: 1,
      }}
      display="flex"
      justifyContent="space-between"
      flexDirection="column">
      <Hidden mdDown>
        <Box
          sx={{
            height: LOGO_HEIGHT,
            paddingTop: `${LOGO_PADDING_TOP}px`,
          }}>
          {logo}
        </Box>
      </Hidden>
      <Box
        display="flex"
        flexDirection="column"
        sx={{
          height: `calc(100vh - ${
            LOGO_HEIGHT +
            LOGO_PADDING_TOP +
            BOTTOM_SECTION_ELEMENT_HEIGHT *
              ((bottomNavigationItems?.length ?? 0) + 1 + (contacts ? 1 : 0))
          }px)`,
          paddingTop: 6,
        }}>
        {navigationItems.map((item) => (
          <Link
            className={comparePathNames(pathname, item.path) ? "active" : ""}
            key={`${item.name}${item.path}`}
            sx={{
              paddingY: 0.75,
              "&.active, &:hover": {
                color: "primary.main",
                textDecoration: "none",
              },
            }}
            variant="body1"
            color="textPrimary"
            onClick={onChangePage}
            component={RouterLink}
            to={item.path}
            underline="none">
            {item.name}
          </Link>
        ))}
      </Box>
      <Box
        display="flex"
        flexDirection="column"
        sx={{
          height:
            BOTTOM_SECTION_ELEMENT_HEIGHT *
            ((bottomNavigationItems?.length ?? 0) + 1 + (contacts ? 1 : 0)),
          paddingTop: 1.5,
        }}>
        {bottomNavigationItems?.map((item) => (
          <Link
            key={item.name}
            href={item.href}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              color: "text.disabled",
              cursor: "pointer",
              paddingBottom: 0.75,
              paddingTop: 0.75,
              textDecoration: "none",
              "&:hover": {
                color: "primary.main",
              },
            }}
            variant="body2">
            {item.name}
          </Link>
        ))}
        {!!contacts && (
          <ContactTooltip
            onClose={handleTooltipClose}
            open={tooltipOpen}
            contacts={contacts}>
            <Typography
              onClick={handleTooltipOpen}
              sx={{
                color: "text.disabled",
                cursor: "pointer",
                paddingBottom: 0.75,
                paddingTop: 0.75,
                "&.active, &:hover": {
                  color: "primary.main",
                },
              }}
              className={tooltipOpen ? " active" : ""}
              variant="body2">
              Contact Us
            </Typography>
          </ContactTooltip>
        )}
        <Typography
          sx={{
            color: "text.disabled",
            cursor: "pointer",
            paddingBottom: 0.75,
            paddingTop: 0.75,
            "&.active, &:hover": {
              color: "primary.main",
            },
          }}
          variant="body2"
          onClick={signOut}>
          Logout
        </Typography>
      </Box>
    </Box>
  );
};

export default SidebarView;
